import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Combobox } from '@bynder/design-system';

import { useTranslate } from '@bynder/localization';
import { InfoContext } from '../../context';

import { API } from '../../helpers/api';
import { AbilityContext } from '../../abilities/Can';
import { UUIDToColdfusionID } from '../../helpers/id';
import { removeFromStorage } from '../Spotlight/storage/browserStorage';
import { BREAKPOINTS } from '../../breakpoints';

const ACCOUNT_PATH = '/account/switch/?id=';

interface AccountOption {
	id: string;
	label: string;
	href: string;
}

export function AccountSwitcher() {
	const [inputValue, setInputValue] = useState('');
	const [searchResults, setSearchResults] = useState<AccountOption[]>([]);
	const ability = useContext(AbilityContext);
	const { translate } = useTranslate();

	const infoContext = useContext(InfoContext);
	const { general, accounts } = infoContext;

	const isSuperadmin = ability.can('superadmin', 'all');

	// Preloaded accounts (non-superadmin users) mapped to the shared option shape.
	const identityOptions = useMemo<AccountOption[]>(
		() =>
			accounts.map((account) => ({
				id: account.account_id,
				label: account.label,
				href:
					ACCOUNT_PATH +
					(account.account_id ? UUIDToColdfusionID(account.account_id) : '') +
					'&module=&currentEvent=account.dashboard',
			})),
		[accounts],
	);

	const onSelectChange = async (value: string) => {
		setInputValue(value);

		// Non-superadmin users filter their preloaded accounts client-side.
		if (!isSuperadmin) {
			return;
		}

		if (value.trim() === '') {
			setSearchResults([]);

			return;
		}

		const results = await API.getAccounts(value);

		setSearchResults(
			results.map((account) => ({
				id: account.id,
				label: account.label,
				href: ACCOUNT_PATH + account.id,
			})),
		);
	};

	const onSelectAccount = (account: AccountOption) => {
		if (!account) {
			return;
		}

		// Clear the activeFilters from session storage when switching accounts, to prevent filters from the previous account being applied to the new account
		removeFromStorage('activeFilters');
		window.location.href = account.href;
	};

	// Superadmins can always search; other users only switch when they have more than one account.
	if (!isSuperadmin && accounts.length <= 1) {
		return null;
	}

	const query = inputValue.trim().toLowerCase();
	const options = isSuperadmin
		? searchResults
		: identityOptions.filter((account) => account.label.toLowerCase().includes(query));

	return (
		<SelectContainer>
			<Combobox
				openOn="focus"
				placeholder={general.accountName}
				value={inputValue}
				onChange={onSelectChange}
				isAnyValueAllowed
			>
				{options.length > 0 ? (
					options.map((account) => (
						<Combobox.Item key={account.id} onClick={() => onSelectAccount(account)}>
							{account.label}
						</Combobox.Item>
					))
				) : (
					<Combobox.Text>{translate('noresults')}</Combobox.Text>
				)}
			</Combobox>
		</SelectContainer>
	);
}

const SelectContainer = styled.div`
	position: absolute;
	margin: 7px 0 0 0;
	z-index: 120;

	& input {
		width: 232px;
		height: 32px !important;
	}

	@media (max-width: ${BREAKPOINTS.TABLET}px) {
		position: static;
	}
`;
